import React, { useCallback, useMemo } from "react";
import { Pressable, StyleSheet, Text, TextInput, View } from "react-native";

const CHURN_REASONS = [
  {
    id: "NOT_FINDING_MATCH",
    label: "Not finding what I'm looking for",
    icon: "🔍",
  },
  { id: "FOUND_SOMEONE", label: "I found someone", icon: "❤️" },
  { id: "PRIVACY_CONCERNS", label: "Privacy concerns", icon: "🔒" },
  { id: "TOO_MANY_NOTIFICATIONS", label: "Too many notifications", icon: "🔔" },
  { id: "APP_QUALITY", label: "App quality / bugs", icon: "🐛" },
  { id: "TAKING_A_BREAK", label: "Taking a break", icon: "☕" },
  { id: "OTHER", label: "Other", icon: "💬" },
] as const;

const MIN_FEEDBACK_LENGTH = 20;
const MAX_FEEDBACK_LENGTH = 500;

type ChurnReasonSelectorProps = {
  reasonId: string | null;
  onReasonIdChange: (id: string | null) => void;
  userFeedback: string;
  onUserFeedbackChange: (text: string) => void;
};

function ChurnReasonSelector({
  reasonId,
  onReasonIdChange,
  userFeedback,
  onUserFeedbackChange,
}: ChurnReasonSelectorProps): React.JSX.Element {
  const handleSelect = useCallback(
    (id: string) => {
      onReasonIdChange(reasonId === id ? null : id);
    },
    [reasonId, onReasonIdChange],
  );

  const feedbackLength = userFeedback.trim().length;
  const isFeedbackTooShort =
    feedbackLength > 0 && feedbackLength < MIN_FEEDBACK_LENGTH;

  const feedbackHint = useMemo(() => {
    if (feedbackLength === 0) {
      return `Minimum ${MIN_FEEDBACK_LENGTH} characters`;
    }
    if (isFeedbackTooShort) {
      return `${MIN_FEEDBACK_LENGTH - feedbackLength} more characters needed`;
    }
    return `${MAX_FEEDBACK_LENGTH - userFeedback.length} characters remaining`;
  }, [feedbackLength, isFeedbackTooShort, userFeedback.length]);

  return (
    <View style={styles.container}>
      <Text style={styles.header}>We are sorry to see you go</Text>
      <Text style={styles.subtitle}>
        Please let us know why you are leaving
      </Text>

      <View style={styles.chipGrid}>
        {CHURN_REASONS.map((reason) => {
          const isSelected = reasonId === reason.id;
          return (
            <Pressable key={reason.id} onPress={() => handleSelect(reason.id)}>
              <View style={[styles.chip, isSelected && styles.chipSelected]}>
                <Text style={styles.chipIcon}>{reason.icon}</Text>
                <Text
                  style={[
                    styles.chipLabel,
                    isSelected && styles.chipLabelSelected,
                  ]}
                >
                  {reason.label}
                </Text>
              </View>
            </Pressable>
          );
        })}
      </View>

      <View style={styles.feedbackContainer}>
        <Text style={styles.feedbackLabel}>
          Tell us more about why you are leaving
        </Text>
        <TextInput
          style={[
            styles.feedbackInput,
            isFeedbackTooShort && styles.feedbackInputWarning,
          ]}
          multiline
          value={userFeedback}
          onChangeText={onUserFeedbackChange}
          maxLength={MAX_FEEDBACK_LENGTH}
          placeholder="Your feedback helps us improve Kai for everyone..."
          placeholderTextColor="#9CA3AF"
          textAlignVertical="top"
        />
        <Text
          style={[
            styles.feedbackHint,
            isFeedbackTooShort && styles.feedbackHintWarning,
          ]}
        >
          {feedbackHint}
        </Text>
      </View>
    </View>
  );
}

export const CHURN_MIN_FEEDBACK_LENGTH = MIN_FEEDBACK_LENGTH;

const styles = StyleSheet.create({
  container: {
    marginBottom: 24,
  },
  header: {
    fontSize: 20,
    fontWeight: "600",
    color: "#1F2937",
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: "#6B7280",
    marginBottom: 16,
  },
  chipGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  chip: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F3F4F6",
    borderRadius: 20,
    borderWidth: 1,
    borderColor: "#F3F4F6",
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginRight: 8,
    marginBottom: 8,
  },
  chipSelected: {
    backgroundColor: "#e1f5fe",
    borderColor: "#01579b",
  },
  chipIcon: {
    marginRight: 6,
  },
  chipLabel: {
    fontSize: 14,
    fontWeight: "500",
    color: "#374151",
  },
  chipLabelSelected: {
    color: "#01579b",
  },
  feedbackContainer: {
    marginTop: 8,
  },
  feedbackLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#4A5568",
    marginBottom: 8,
  },
  feedbackInput: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    minHeight: 100,
    color: "#1F2937",
  },
  feedbackInputWarning: {
    borderColor: "#F59E0B",
  },
  feedbackHint: {
    fontSize: 12,
    color: "#9CA3AF",
    marginTop: 4,
    textAlign: "right",
  },
  feedbackHintWarning: {
    color: "#F59E0B",
  },
});

export default ChurnReasonSelector;
